import React, { useState, useEffect, useCallback } from 'react';
import { WikiPage, WikiPageHistory as WikiPageHistoryType, CreateWikiPageDto, UpdateWikiPageDto } from '../../types/wiki';
import { wikiService } from '../../services/wikiService';
import { apiService } from '../../services/api';
import { WikiPageDisplay } from './WikiPageDisplay';
import { WikiPageForm } from './WikiPageForm';
import { WikiPageHistory } from './WikiPageHistory';
import { Archive, ChevronLeft, CircleCheck, Ellipsis, FileText, Lock, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button, Card, EmptyState, IconButton, Menu, Pill, SectionHeader, Skeleton } from '../ui';
import { timeAgo, formatDateTime, WIKI_STATUS } from './wikiFormat';

interface WikiPageManagerProps {
  entityType: 'stakeholder' | 'organization';
  entityId: string;
  entityName?: string;
  className?: string;
  externalCreateMode?: boolean;
  onCreateModeChange?: (isCreateMode: boolean) => void;
}

type ViewMode = 'list' | 'view' | 'create' | 'edit' | 'history';

export const WikiPageManager: React.FC<WikiPageManagerProps> = ({
  entityType,
  entityId,
  entityName,
  className = '',
  externalCreateMode,
  onCreateModeChange,
}) => {
  const [wikiPages, setWikiPages] = useState<WikiPage[]>([]);
  const [selectedWikiPage, setSelectedWikiPage] = useState<WikiPage | null>(null);
  const [wikiHistory, setWikiHistory] = useState<WikiPageHistoryType[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load wiki pages for the entity
  const loadWikiPages = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const pages = await wikiService.getWikiPagesByEntity(entityType, entityId);
      setWikiPages(pages);

      // Auto-select the first page if there's only one
      if (pages.length === 1) {
        setSelectedWikiPage(pages[0]);
        setViewMode('view');
      }
    } catch (err) {
      setError('Failed to load wiki pages');
      console.error('Error loading wiki pages:', err);
    } finally {
      setIsLoading(false);
    }
  }, [entityType, entityId]);

  const loadWikiHistory = useCallback(async (wikiPageId: string) => {
    try {
      setIsLoading(true);
      const history = await wikiService.getWikiPageHistory(wikiPageId);
      setWikiHistory(history);
    } catch (err) {
      setError('Failed to load history');
      console.error('Error loading wiki history:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleWikiPageSubmit = useCallback(async (data: CreateWikiPageDto | UpdateWikiPageDto) => {
    try {
      setIsLoading(true);
      if (selectedWikiPage) {
        const updated = await wikiService.updateWikiPage(selectedWikiPage.id, data as UpdateWikiPageDto);
        setWikiPages(prev => prev.map(p => (p.id === updated.id ? updated : p)));
        setSelectedWikiPage(updated);
      } else {
        const created = await wikiService.createWikiPage(data as CreateWikiPageDto);
        setWikiPages(prev => [...prev, created]);
        setSelectedWikiPage(created);
      }
      setViewMode('view');
      onCreateModeChange?.(false);
    } catch (err) {
      setError(selectedWikiPage ? 'Failed to update page' : 'Failed to create page');
      console.error('Error submitting wiki page:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [selectedWikiPage, onCreateModeChange]);

  const handleRestoreVersion = useCallback(async (version: number) => {
    if (!selectedWikiPage) return;
    try {
      setIsLoading(true);
      const restored = await wikiService.restoreWikiPageVersion(selectedWikiPage.id, version);
      setWikiPages(prev => prev.map(p => (p.id === restored.id ? restored : p)));
      setSelectedWikiPage(restored);
      setViewMode('view');
    } catch (err) {
      setError('Failed to restore version');
    } finally {
      setIsLoading(false);
    }
  }, [selectedWikiPage]);

  const handleDeleteWikiPage = useCallback(async (e: React.MouseEvent, wikiPage: WikiPage) => {
    e.stopPropagation();
    if (wikiPage.isLocked) {
      setError('Cannot delete a locked page. Unlock it from the Wiki Library first.');
      return;
    }
    if (!window.confirm(`Delete "${wikiPage.title}"? This cannot be undone.`)) return;
    try {
      setIsLoading(true);
      await wikiService.deleteWikiPage(wikiPage.id);
      setWikiPages(prev => prev.filter(p => p.id !== wikiPage.id));
      if (selectedWikiPage?.id === wikiPage.id) {
        setSelectedWikiPage(null);
        setViewMode('list');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to delete page');
    } finally {
      setIsLoading(false);
    }
  }, [selectedWikiPage]);

  const handleChangeStatus = useCallback(async (e: React.MouseEvent, wikiPage: WikiPage, newStatus: string) => {
    e.stopPropagation();
    try {
      setIsLoading(true);
      let updated: WikiPage;
      if (newStatus === 'approved' || newStatus === 'rejected') {
        // POST /api/wiki/:id/approve | /reject (admins), through the authenticated API client
        const action = newStatus === 'approved' ? 'approve' : 'reject';
        const response = await apiService.post(`/api/wiki/${wikiPage.id}/${action}`);
        const body = response?.data || {};
        // The response is the raw entity; keep the mapped fields (entity, creator) we already have
        updated = {
          ...wikiPage,
          status: body.status || newStatus,
          approvedBy: body.approvedBy ?? wikiPage.approvedBy,
          approvedAt: body.approvedAt ?? wikiPage.approvedAt,
          updatedAt: body.updatedAt || wikiPage.updatedAt,
          version: body.version || wikiPage.version,
        };
      } else {
        updated = await wikiService.updateWikiPage(wikiPage.id, { status: newStatus } as any);
      }
      setWikiPages(prev => prev.map(p => (p.id === updated.id ? updated : p)));
      if (selectedWikiPage?.id === wikiPage.id) setSelectedWikiPage(updated);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to change status');
    } finally {
      setIsLoading(false);
    }
  }, [selectedWikiPage]);

  const handleBackToList = useCallback(() => {
    setSelectedWikiPage(null);
    setViewMode('list');
    setError(null);
    onCreateModeChange?.(false);
  }, [onCreateModeChange]);

  useEffect(() => {
    loadWikiPages();
  }, [loadWikiPages]);

  // Allow parent to trigger create mode
  useEffect(() => {
    if (externalCreateMode) {
      setSelectedWikiPage(null);
      setViewMode('create');
    }
  }, [externalCreateMode]);

  if (error) {
    return (
      <div className={`font-ui ${className}`}>
        <p role="alert" className="flex items-center justify-between gap-3 rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">
          {error}
          <button
            type="button"
            onClick={() => { setError(null); loadWikiPages(); }}
            className="flex-shrink-0 font-medium underline-offset-2 hover:underline"
          >
            Try again
          </button>
        </p>
      </div>
    );
  }

  const backLink = (label: React.ReactNode, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      className="-ml-1 mb-2 inline-flex h-7 items-center gap-0.5 rounded-full pl-0.5 pr-2 text-[13px] text-neutral-500 transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40"
    >
      <ChevronLeft className="h-4 w-4" strokeWidth={1.75} />
      {label}
    </button>
  );

  // Menu items carry no event; the handlers only use it to stop row clicks
  const noEvent = { stopPropagation: () => {} } as React.MouseEvent;

  /* ─── Create / Edit form ────────────────────────────────────────────── */
  if (viewMode === 'create') {
    return (
      <div className={`font-ui ${className}`}>
        {backLink('Pages', handleBackToList)}
        <SectionHeader title="New page" className="mb-3" />
        <WikiPageForm
          entityType={entityType}
          entityId={entityId}
          onSubmit={handleWikiPageSubmit}
          onCancel={handleBackToList}
          isLoading={isLoading}
        />
      </div>
    );
  }
  if (viewMode === 'edit' && selectedWikiPage) {
    return (
      <div className={`font-ui ${className}`}>
        {backLink(selectedWikiPage.title, () => setViewMode('view'))}
        <SectionHeader title="Edit page" className="mb-3" />
        <WikiPageForm
          wikiPage={selectedWikiPage}
          entityType={entityType}
          entityId={entityId}
          onSubmit={handleWikiPageSubmit}
          onCancel={() => setViewMode('view')}
          isLoading={isLoading}
        />
      </div>
    );
  }

  /* ─── History ───────────────────────────────────────────────────────── */
  if (viewMode === 'history' && selectedWikiPage) {
    return (
      <Card padded className={`font-ui ${className}`}>
        <WikiPageHistory
          history={wikiHistory}
          currentVersion={selectedWikiPage.version}
          onRestore={handleRestoreVersion}
          onClose={() => setViewMode('view')}
          isLoading={isLoading}
        />
      </Card>
    );
  }

  /* ─── View single page ──────────────────────────────────────────────── */
  if (viewMode === 'view' && selectedWikiPage) {
    return (
      <div className={`font-ui ${className}`}>
        {/* Back button if multiple pages */}
        {wikiPages.length > 1 && backLink(
          <>All pages <span className="ml-1 tabular-nums text-neutral-400">{wikiPages.length}</span></>,
          handleBackToList,
        )}
        <WikiPageDisplay
          wikiPage={selectedWikiPage}
          onEdit={() => setViewMode('edit')}
          onViewHistory={async () => {
            await loadWikiHistory(selectedWikiPage.id);
            setViewMode('history');
          }}
        />
      </div>
    );
  }

  const startCreate = () => { setSelectedWikiPage(null); setViewMode('create'); };

  /* ─── Page list ─────────────────────────────────────────────────────── */
  return (
    <div className={`font-ui text-ink ${className}`}>
      <SectionHeader
        title={
          <>
            Pages
            {entityName && <span className="text-neutral-400"> · {entityName}</span>}
          </>
        }
        aside={
          <Button size="xs" variant="secondary" onClick={startCreate}>
            <Plus className="h-3.5 w-3.5" strokeWidth={2} />
            New
          </Button>
        }
      />

      {isLoading ? (
        <Card divided aria-hidden="true">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex h-14 items-center gap-3 px-4">
              <Skeleton className="h-4 w-4 rounded" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className={`h-3.5 ${i % 2 ? 'w-1/3' : 'w-1/2'}`} />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
          ))}
        </Card>
      ) : wikiPages.length === 0 ? (
        <Card>
          <EmptyState
            icon={FileText}
            title="No pages yet"
            action={
              <Button size="sm" variant="secondary" onClick={startCreate}>
                <Plus className="h-4 w-4" strokeWidth={2} />
                New page
              </Button>
            }
          />
        </Card>
      ) : (
        <Card divided role="list">
          {wikiPages.map((page) => {
            const status = WIKI_STATUS[page.status] || WIKI_STATUS.draft;
            return (
              <div
                key={page.id}
                role="listitem"
                className="group flex items-center gap-2 pl-4 pr-2 transition-colors hover:bg-neutral-50"
              >
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => { setSelectedWikiPage(page); setViewMode('view'); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedWikiPage(page);
                      setViewMode('view');
                    }
                  }}
                  className="flex min-h-[56px] min-w-0 flex-1 cursor-pointer items-center gap-3 py-2 focus-visible:outline-none"
                >
                  <FileText className="h-4 w-4 flex-shrink-0 text-neutral-400" strokeWidth={1.75} />
                  <div className="min-w-0 flex-1">
                    <p className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate text-[14px] text-ink sm:text-[13px]">{page.title}</span>
                      {page.isLocked && <Lock className="h-3 w-3 flex-shrink-0 text-neutral-400" strokeWidth={2} aria-label="Locked" />}
                    </p>
                    <p className="mt-0.5 text-[12px] tabular-nums text-neutral-400" title={formatDateTime(page.updatedAt)}>
                      v{page.version} · {timeAgo(page.updatedAt)}
                    </p>
                  </div>
                  {page.status !== 'approved' && <Pill tone={status.tone}>{status.label}</Pill>}
                </div>
                <Menu
                  items={[
                    { label: 'Edit', icon: Pencil, onSelect: () => { setSelectedWikiPage(page); setViewMode('edit'); } },
                    ...(page.status !== 'approved'
                      ? [{ label: 'Approve', icon: CircleCheck, onSelect: () => handleChangeStatus(noEvent, page, 'approved') }]
                      : []),
                    // The API cannot reject a page that is already approved
                    ...(page.status !== 'rejected' && page.status !== 'approved'
                      ? [{ label: 'Archive', icon: Archive, onSelect: () => handleChangeStatus(noEvent, page, 'rejected') }]
                      : []),
                    {
                      label: page.isLocked ? 'Locked' : 'Delete',
                      icon: Trash2,
                      danger: true,
                      disabled: page.isLocked,
                      onSelect: () => handleDeleteWikiPage(noEvent, page),
                    },
                  ]}
                  trigger={(props) => (
                    <IconButton label={`Actions for ${page.title}`} size="sm" className="text-neutral-400" {...props}>
                      <Ellipsis className="h-4 w-4" strokeWidth={1.75} />
                    </IconButton>
                  )}
                />
              </div>
            );
          })}
        </Card>
      )}
    </div>
  );
};
